import type { ReactNode } from 'react';
import { cardClasses } from './Card.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';

/**
 * O AVISO QUE ABRE A TELA — o fato que muda a conversa antes de qualquer
 * número dela. Nasceu em 21/10/2026 para o sinistro na ficha do cliente da
 * agenda: o carro em tratativa é a primeira coisa que o cliente pergunta, e o
 * `Card tone="alerta"` que o dizia foi reprovado pelo dono — o cartão INTEIRO
 * tingido de âmbar lia, no escuro, como um bloco marrom, e o título em 13px
 * tinha o peso de qualquer outro cartão da pilha.
 *
 * ## Onde está a diferença para o `Alert` e para o `Card` tingido
 *
 * - **O fundo NÃO é tingido.** A cor mora em três lugares só: a pastilha
 *   CHEIA com o símbolo, o rótulo do assunto e a borda. O fundo `-suave` do
 *   âmbar é, no escuro, um marrom que se confunde com o laranja da marca —
 *   foi ele que o dono reprovou, na primeira versão desta peça também (a faixa
 *   de cima ainda era tingida). Sobre a superfície do cartão, os dados têm o
 *   contraste de sempre e o âmbar cheio da pastilha é o que salta.
 * - **A pastilha é preenchida**, e é isso que destaca: o `Alert` desenha o
 *   ícone em traço, do tamanho do texto, e some numa tela cheia de cartões.
 * - **A borda é inteira**, nunca lateral (o `DESIGN.md` bane a lateral).
 *
 * O `Alert` continua sendo o aviso de LINHA — "pode faltar boleto nesta
 * lista". Este é o de topo, um por tela, e só quando o fato é a notícia.
 */
type NoticeTone = 'alerta' | 'erro';

/*
 * As chaves são os VALORES de tom, não nomes nossos — o mapa é indexado pela
 * união literal, como o de `Alert`.
 */
// @valor
const TONES: Record<
  NoticeTone,
  { border: string; tile: string; label: string; icon: IconName }
> = {
  alerta: {
    border: 'border-alerta',
    tile: 'bg-alerta text-texto-inverso',
    label: 'text-alerta',
    icon: 'atencao',
  },
  erro: {
    border: 'border-erro',
    tile: 'bg-erro text-texto-inverso',
    label: 'text-erro',
    icon: 'atencao',
  },
};

export function NoticeBanner({
  tone = 'alerta',
  icon,
  eyebrow,
  title,
  aside,
  children,
  className,
}: {
  tone?: NoticeTone;
  /** O símbolo da pastilha. Sem ele, o triângulo de atenção. */
  icon?: IconName;
  /** O assunto, em versalete no tom — "Atendimento com o Dept. Sinistro". */
  eyebrow: string;
  /** A frase que é a notícia. */
  title: ReactNode;
  /** O canto direito da faixa — uma contagem, um selo. */
  aside?: ReactNode;
  /** Os dados do fato, na superfície do cartão. Normalmente `Facts`. */
  children?: ReactNode;
  className?: string;
}) {
  const t = TONES[tone];
  return (
    <section
      role={tone === 'erro' ? 'alert' : 'status'}
      aria-label={eyebrow}
      className={cardClasses({
        padding: false,
        className: cn('overflow-hidden', t.border, className),
      })}
    >
      <header className={cn('flex flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-3.5')}>
        <span
          aria-hidden="true"
          className={cn('grid size-11 shrink-0 place-items-center rounded-borda', t.tile)}
        >
          <Icon nome={icon ?? t.icon} size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={cn('text-[0.6875rem] font-bold tracking-wide uppercase', t.label)}
          >
            {eyebrow}
          </p>
          <p className="mt-0.5 text-base font-bold text-texto">{title}</p>
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>
      {children && (
        <div className="border-t border-borda-suave px-4 py-4">{children}</div>
      )}
    </section>
  );
}
